import type { AdminWorkspace, PublicWorkspace } from '../types'
import { STANDARD_CASE_STUDY_ASSETS, STANDARD_PRODUCT_ASSETS } from './standard-content'

export const CODING_TEMPLE_CURRENT_DEAL_ID = '62886337918'
export const CODING_TEMPLE_PUBLIC_ID = 'ct-7a4f2c9e8d31-preview'

const publicWorkspace: PublicWorkspace = {
  id: '9d3dcf13-8c0c-4ff3-8d3e-9cbe4d11a4d0',
  publicId: CODING_TEMPLE_PUBLIC_ID,
  accountName: 'Coding Temple',
  accountLogoUrl: '/images/logos/coding-temple.webp',
  customerSegment: 'bootcamp_workforce',
  phase: 'sales',
  pricingModel: 'license',
  pricingModes: 'auto',
  firstCallRecorded: false,
  overview: 'A shared plan to make career support easier for jobseekers to use, easier for the team to operate, and easier for leadership to measure.',
  recap: 'Coding Temple is evaluating Prentus as the connected system for jobseeker activation, job-search support, employer engagement, and credible outcomes reporting. The goal is to reduce friction for jobseekers while giving the career team one dependable view of weekly activity and results.',
  currentNextStep: 'Align on the launch scope, reporting requirements, and budget during the August 24 follow-up.',
  precallOnepager: null,
  successContent: null,
  projects: [],
  renewal: null,
  orderForms: [],
  prequalificationOnly: false,
  businessCase: [
    { id: 'ct-bc-1', body: 'Increase activation with fast onboarding, clear next steps, and job-search workflows jobseekers can use without extra coaching.' },
    { id: 'ct-bc-2', body: 'Rebuild trust in job discovery with relevant matches, automatic expiry handling, and program-specific guidance.' },
    { id: 'ct-bc-3', body: 'Give employers a frictionless way to review talent while keeping outreach and jobseeker action visible to the career team.' },
    { id: 'ct-bc-4', body: 'Turn weekly activity, applications, and verified outcomes into reporting leadership can use for operating reviews and partner conversations.' },
  ],
  members: [
    {
      id: 'ct-member-ryan',
      side: 'customer',
      name: 'Ryan Taylor',
      title: 'Leadership, Coding Temple Holdings',
      email: 'ryant@codingtemple.com',
      bookingUrl: null,
      avatarUrl: null,
    },
    {
      id: 'ct-member-sara',
      side: 'customer',
      name: 'Sara Voyles',
      title: 'Career Services Lead',
      email: 'sarav@codingtemple.com',
      bookingUrl: null,
      avatarUrl: null,
    },
    {
      id: 'ct-member-rod',
      side: 'prentus',
      name: 'Rod Danan',
      title: 'Founder & CEO, Prentus',
      email: 'rod.danan@prentus.com',
      bookingUrl: 'https://cal.com/rod-danan',
      avatarUrl: '/images/team/rod-danan.webp',
    },
  ],
  tasks: [
    { id: 'ct-task-1', title: 'Align internally on requirements and budget', description: 'Confirm the launch priorities for activation, employer engagement, reporting, and integrations.', ownerName: 'Ryan + Sara', dueDate: '2026-08-21', status: 'in_progress' },
    { id: 'ct-task-2', title: 'Confirm the new jobs engine and outcomes roadmap', description: 'Share the near-term product milestones discussed in the demo.', ownerName: 'Prentus', dueDate: '2026-08-21', status: 'in_progress' },
    { id: 'ct-task-3', title: 'Review partnership scope and pricing', description: 'Use the follow-up to align the initial license count, modules, and implementation plan.', ownerName: 'Coding Temple + Prentus', dueDate: '2026-08-24', status: 'open' },
    { id: 'ct-task-4', title: 'Map launch workflows and integrations', description: 'Define the jobseeker journey, reporting cadence, and required system connections.', ownerName: 'Sara + Rod', dueDate: '2026-08-28', status: 'open' },
  ],
  assets: [
    {
      id: 'ct-recording-july-20',
      sectionKey: 'recordings',
      type: 'video',
      title: 'Coding Temple and Prentus discovery',
      description: 'July 20, 2026. Initial conversation about Coding Temple’s operating priorities, career outcomes, and reporting needs.',
      url: 'https://fathom.video/share/yN8qZKzAWxY5WVDny4dq_MPhrrtHDQwU',
      source: 'fathom',
      metadata: { duration: '45 min' },
    },
    {
      id: 'ct-recording-august-17',
      sectionKey: 'recordings',
      type: 'video',
      title: 'Coding Temple and Prentus demo',
      description: 'August 17, 2026. Product demonstration covering activation, job discovery, employer workflows, outcomes reporting, and implementation.',
      url: 'https://fathom.video/share/Z6DiSZUCSsW4S_CWcs_J72J1rKDpySWJ',
      source: 'fathom',
      metadata: { duration: '50 min' },
    },
    ...STANDARD_PRODUCT_ASSETS,
    ...STANDARD_CASE_STUDY_ASSETS,
  ],
  calculatorInputs: {
    annualLicenses: 500,
    termYears: 1,
    selectedModules: ['matching', 'employer', 'outcomes'],
    automatedWorkflows: false,
    supportTier: 'standard',
    implementationScope: 1,
  },
  viewerEmail: 'preview@codingtemple.com',
  updatedAt: '2026-08-18T03:20:00.000Z',
}

export const CODING_TEMPLE_PUBLIC_WORKSPACE = publicWorkspace

export const CODING_TEMPLE_ADMIN_WORKSPACE: AdminWorkspace = {
  ...publicWorkspace,
  status: 'published',
  companyHubspotId: 'coding-temple-holdings-current',
  deals: [{ id: CODING_TEMPLE_CURRENT_DEAL_ID, name: 'Coding Temple Holdings', stage: '155227470' }],
  allowedDomains: ['codingtemple.com'],
  pricingVisibility: 'visible',
  pendingApprovals: 0,
  suggestions: [],
  sandboxRequests: [],
  qualification: null,
  followUp: null,
  reviewWarnings: [],
  members: publicWorkspace.members.map((member) => ({
    ...member,
    stakeholderRole: member.id === 'ct-member-ryan'
      ? 'economic_buyer'
      : member.id === 'ct-member-sara'
        ? 'champion'
        : null,
  })),
  tasks: publicWorkspace.tasks.map((task) => ({ ...task, visibility: 'published' })),
  assets: publicWorkspace.assets.map((asset) => ({ ...asset, visibility: 'published' })),
  businessCase: publicWorkspace.businessCase.map((point) => ({
    ...point,
    visibility: 'published',
    sourceRefs: [{ source: 'hubspot', id: CODING_TEMPLE_CURRENT_DEAL_ID }],
  })),
  timeline: [
    {
      id: 'ct-timeline-call-july-20',
      source: 'fathom',
      kind: 'meeting',
      title: 'Coding Temple and Prentus discovery',
      occurredAt: '2026-07-20T20:02:47.000Z',
      summary: 'Ryan and Rod discussed Coding Temple’s current operating priorities, active jobseeker volume, reporting requirements, and the need for a scalable career outcomes system.',
      body: null,
      sourceUrl: 'https://fathom.video/share/yN8qZKzAWxY5WVDny4dq_MPhrrtHDQwU',
      participants: ['Ryan Taylor', 'Rod Danan'],
    },
    {
      id: 'ct-timeline-call-august-17',
      source: 'fathom',
      kind: 'meeting',
      title: 'Coding Temple and Prentus demo',
      occurredAt: '2026-08-17T20:01:16.000Z',
      summary: 'Ryan, Sara, Rod, and Ashley reviewed activation, job quality, employer workflows, outcome reporting, AI coaching, and implementation fit.',
      body: null,
      sourceUrl: 'https://fathom.video/share/Z6DiSZUCSsW4S_CWcs_J72J1rKDpySWJ',
      participants: ['Ryan Taylor', 'Sara Voyles', 'Rod Danan', 'Ashley Murphy'],
    },
    {
      id: 'ct-timeline-follow-up',
      source: 'calendar',
      kind: 'meeting',
      title: 'Coding Temple and Prentus follow-up',
      occurredAt: '2026-08-24T19:00:00.000Z',
      summary: 'Scheduled follow-up to align the partnership scope, pricing, implementation priorities, and path forward.',
      body: null,
      sourceUrl: null,
      participants: ['Ryan Taylor', 'Rod Danan'],
    },
  ],
}
